import { Component, OnDestroy } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@salesrun/ngx-dialog';
import { EMPTY, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { IIsResetPasswordPossibleResponse } from '../../models';
import { LoginService } from '../login.service';


@Component({
    selector: 'sr-login-password-reset-page',
    templateUrl: './password-reset-page.view.html',
    styleUrls: ['./password-reset-page.style.scss'],
    standalone: false
})
export class PasswordResetPageComponent implements OnDestroy {
    private _destroy$ = new Subject<boolean>();
    form: FormGroupTyped<{ password: string }>;
    _router: Router;

    loading = false;

    contactName: string;
    userName: string;
    errorMsg: string;
    error = false;
    private resetPasswordKey: string;
    isSetup = false;

    constructor(
        router: Router,
        private activatedRoute: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private _dialog: DialogService,
        private _login: LoginService,
    ) {
        this._router = router;
        this.activatedRoute.data
            .pipe(takeUntil(this._destroy$)).subscribe(data => {
                this.isSetup = data?.setup === true;
            });
        this.activatedRoute.params
            .pipe(takeUntil(this._destroy$)).subscribe(params => {
                this.resetPasswordKey = params?.resetPasswordKey;
            });
        if (this.resetPasswordKey != undefined) {
            this._login.isResetPasswordPossible(this.resetPasswordKey)
                .pipe(
                    catchError(err => {
                        if (!this.isSetup) {
                            this.isBrokenPasswordLink();
                        }
                        else {
                            void this._router.navigate(['expired']);
                        }
                        return of(null);
                    }),
                    finalize(() => {
                        this.loading = false;
                    }),
                    takeUntil(this._destroy$))
                .subscribe({
                    next: (response: IIsResetPasswordPossibleResponse) => {
                        if (response.isExpired) {
                            void this._router.navigate(['expired']);
                        }
                        this.contactName = response.contactName;
                        this.userName = response.userName;
                        this.isSetup = response.isSetup;
                    },
                    error: () => { },
                });
        } else {
            if (!this.isSetup) {
                this.isBrokenPasswordLink();
            }
            else {
                void this._router.navigate(['expired']);
            }
        }

        this.form = this.formBuilder.group({
            password: null,
        }) as FormGroupTyped<{ password: string }>;
    }

    isBrokenPasswordLink(): void {
        this._dialog
            .ok('Your Password link is broken - Please try again', 'Reset Password')
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                void this._router.navigate(['login']);
            });
    }
    showExpiredMessage(): void {
        this._dialog
            .ok('Your Password link expired - Please try again', 'Reset Password')
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                void this._router.navigate(['login/request-password']);
            });
    }

    get buttonText(): string {
        if (this.isSetup) {
            return this.loading ? 'Saving...' : 'Setup password';
        }
        else {
            return this.loading ? 'Changing...' : 'Change password';
        }
    }

    setupPassword(): void {
        if (this.form.valid) {
            this.error = false;
            this.loading = true;
            const newPassword = this.form.value.password;
            this._login.isResetPasswordPossible(this.resetPasswordKey)
                .pipe(
                    catchError(() => void this._router.navigate(['expired'])),
                    takeUntil(this._destroy$))
                .subscribe((response: IIsResetPasswordPossibleResponse) => {
                    if (response.isExpired) {
                        void this._router.navigate(['expired']);
                    } else {
                        this._login.setupPassword(newPassword, this.resetPasswordKey)
                            .pipe(takeUntil(this._destroy$))
                            .subscribe(response => {
                                if (response === true) {
                                    this._login.login(this.userName, newPassword).pipe(
                                        catchError((error: string) => {
                                            this.errorMsg = 'An error occured. Please, try again later.';
                                            if (typeof error === 'string') {
                                                this.errorMsg = error;
                                            } else {
                                                console.error(error);
                                            }
                                            this.error = true;
                                            this.loading = false;
                                            return EMPTY;
                                        }),
                                        takeUntil(this._destroy$))
                                        .subscribe(result => {
                                            location.href = result.redirectUrl;
                                        });
                                } else {
                                    this._login
                                        .snackbar('Error occured while changing password - Please try again');
                                }
                            });
                    }
                });
        }
    }

    resetPassword(): void {
        if (this.form.valid) {
            this.error = false;
            this.loading = true;
            this._login
                .resetPassword(this.form.value.password, this.resetPasswordKey)
                .pipe(
                    catchError(err => {
                        this.errorMsg = err;
                        this.error = true;
                        return of(false);
                    }),
                    finalize(() => {
                        this.loading = false;
                    }),
                    takeUntil(this._destroy$),
                )
                .subscribe(response => {
                    if (response === true) {
                        this._dialog
                            .ok('Your Password has been successfully changed',
                                'Reset Password')
                            .pipe(takeUntil(this._destroy$))
                            .subscribe(() => {
                                void this._router.navigate(['login']);
                            });
                    } else {
                        this._login
                            .snackbar('Error occured while changing password - Please try again');
                    }
                });
        }


    }

    ngOnDestroy(): void {
        this._destroy$.next(true);
        this._destroy$.complete();
    }
}